import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, BookOpen } from 'lucide-react';
import { PublicNavbar } from '../components/brand/PublicNavbar';
import { Footer } from '../components/brand/Footer';
import { Seo } from '../components/seo/Seo';
import {
  Breadcrumb, BreadcrumbList, BreadcrumbItem, BreadcrumbLink, BreadcrumbSeparator, BreadcrumbPage,
} from '../components/ui/breadcrumb';
import { ARTICLES } from '../data/articles';
import { BRAND } from '../lib/siteConfig';
import { breadcrumbSchema } from '../lib/seoSchemas';

const ArticleCard = ({ a, featured = false }) => (
  <Link
    to={`/resources/${a.slug}`}
    className={`cn-card cn-card-hover rounded-3xl overflow-hidden block group ${featured ? 'md:grid md:grid-cols-2' : ''}`}
    data-testid={`article-card-${a.slug}`}
  >
    <div className={`overflow-hidden ${featured ? 'aspect-[4/3] md:aspect-auto md:h-full' : 'aspect-[16/9]'}`}>
      <img
        src={a.heroImage}
        alt={a.heroAlt}
        loading={featured ? 'eager' : 'lazy'}
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
      />
    </div>
    <div className={`p-6 ${featured ? 'md:p-8 flex flex-col justify-center' : ''}`}>
      <div className="flex items-center gap-3 mb-3">
        <span className="cn-eyebrow !mb-0 text-cn-accent">{a.category}</span>
        <span className="text-xs text-cn-text-muted inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {a.readMinutes} min read</span>
      </div>
      <h3 className={`font-display font-semibold text-cn-text leading-snug ${featured ? 'text-2xl md:text-3xl mb-3' : 'text-lg mb-2'}`}>
        {a.title}
      </h3>
      <p className="text-sm text-cn-text-muted leading-relaxed line-clamp-3">{a.excerpt}</p>
      <span className="mt-4 text-sm text-cn-primary font-semibold inline-flex items-center gap-1">
        Read guide <ArrowRight className="w-3.5 h-3.5" />
      </span>
    </div>
  </Link>
);

export default function ResourcesPage() {
  const [featured, ...rest] = ARTICLES;
  const schema = breadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Resources', path: '/resources' },
  ]);

  return (
    <div className="min-h-screen bg-cn-bg" data-testid="resources-page">
      <Seo
        title={`Family Care Guides & Resources | ${BRAND}`}
        description="Trusted, practical guides for families navigating home care — hiring a nurse, goals of care, preventing caregiver burnout, paying for care, and safe hospital-to-home recovery."
        path="/resources"
        keywords={['home care guides', 'family caregiver resources', 'how to hire a nurse', 'paying for home care']}
        schema={schema}
      />
      <PublicNavbar />

      <div className="cn-container pt-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Resources</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      {/* Header */}
      <section className="cn-container pt-8 pb-12">
        <div className="max-w-2xl">
          <div className="cn-eyebrow mb-3 flex items-center gap-2"><BookOpen className="w-4 h-4 text-cn-accent" /> Resources & guides</div>
          <h1 className="cn-display text-4xl sm:text-5xl lg:text-6xl text-cn-text mb-5">Care wisdom for families.</h1>
          <p className="text-lg text-cn-text-muted leading-relaxed">
            Honest, practical guides — written and reviewed by licensed clinicians — to help you make confident
            decisions about caring for the people you love.
          </p>
        </div>
      </section>

      {/* Featured */}
      <section className="cn-container pb-10">
        <ArticleCard a={featured} featured />
      </section>

      {/* Grid */}
      <section className="cn-container pb-16">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {rest.map((a) => <ArticleCard key={a.slug} a={a} />)}
        </div>
      </section>

      {/* CTA band */}
      <section className="cn-container pb-20">
        <div className="rounded-3xl bg-cn-primary text-white p-10 md:p-14 text-center">
          <h2 className="cn-display text-3xl md:text-4xl text-white mb-3">Ready to find trusted care?</h2>
          <p className="text-white/80 max-w-xl mx-auto mb-7">
            Browse verified, background-checked clinicians near you — or let our AI assistant match you in about a minute.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/patient/find-clinicians" className="cn-btn bg-white text-cn-primary hover:bg-cn-soft" data-testid="resources-cta-marketplace">
              Find a clinician <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/auth" className="cn-btn cn-btn-ghost border border-white/30 text-white hover:bg-white/10" data-testid="resources-cta-auth">
              Get matched by AI
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
